import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled, { keyframes } from "styled-components/macro";
import { ChevronLeft, ChevronRight } from "react-feather";
import { SurfaceLayoutGroup } from "../../api/generated/models";
import { useBreakpoint } from "../../utilities/media-js-breakpoints";

import { getActiveLayoutGroup, getGuiTranslations, getIconUrl, getModelId } from "../../store/sagas/selectors";
import { SET_ACTIVE_LAYOUT_GROUP } from "../../store/actions/configurationActions";

// total size of image include som margin and borders.
const IMAGE_TOTAL_SIZE = 175;

const onLoadAnimation = keyframes`
    0% {bottom: 105px; opacity:0;}
    100% {bottom: 125px; opacity:1;}
`;

const onLoadAnimationMobile = keyframes`
    0% {bottom: 32px; opacity:0;}
    100% {bottom: 55px; opacity:1;}
`;

const onLoadLayoutGroup = keyframes`
    0% {background: rgba(255, 255, 255, 0)}
    50% {background: rgba(255, 255, 255, 0.5)}
    100% {background: rgba(255, 255, 255, 0)}
`;

const LayoutContainer = styled.div<LayoutContainerProps>`
    display: block;
    position: absolute;
    bottom: ${props => (props.isMobile ? "55px" : "125px")};
    z-index: 5;
    width: 100%;
    display: flex;
    flex-direction: ${props => (props.minified ? "row" : "column")};
    justify-content: center;
    align-items: center;
    height: ${props => (props.isMobile ? "50%" : "190px")};
    animation-name: ${props => (props.isMobile ? onLoadAnimationMobile : onLoadAnimation)};
    animation-duration: 0.5s;
    animation-timing-function: ease-in-out;
    opacity: 1;
    pointer-events: none;
    ${({ theme }) => theme.bp.xxxl`
        transform: scale(1.4, 1.4);
        transform-origin: bottom center;
    `}
`;

const LayoutGroupContainer = styled.div`
    pointer-events: none;
`;

const LayoutImageContainer = styled.div`
    pointer-events: auto;
`;

const LayoutGroupActiveContainer = styled.div<LayoutGroupContainerProps>`
    display: flex;
    padding: 10px 15px;
    border-radius: 4px;
    animation-name: ${props => (props.isActive ? onLoadLayoutGroup : "")};
    animation-duration: 0.8s;
    animation-timing-function: ease-in-out;
    pointer-events: none;
`;

const LayoutGroup = styled.div<LayoutGroupProps>`
    display: flex;
    flex-direction: row;
    align-items: center;
    ${props => (props.isMobile ? "flex-wrap: wrap; justify-content: center;" : "")}
`;

const MissingImage = styled.div<LayoutProps>`
    font-weight: 400;
    width: 160px;
    height: 90px;
    background: #eee;
    margin: 0px 5px 10px 5px;
    outline: ${props => (props.selected ? "Thick solid var(--primary-color)" : "Thin solid white")};
    box-shadow: ${props => (props.selected ? "0px 6px 12px #515151" : "0px 2px 8px #515151")};
    transition: all 0.2s ease-in-out;
    word-break: break-all;
    max-width: ${props => (props.isMobile ? "120px" : "160px")};
    @media (hover) {
        &:hover {
            outline: Thick solid var(--primary-hover-color);
            cursor: ${props => (props.selected ? "default" : "pointer")};
            box-shadow: 0px 6px 12px #515151;
        }
    }
`;

const Layout = styled.img<LayoutProps>`
    font-weight: ${props => (props.selected ? "800" : "400")};
    height: ${props => (props.isMobile ? "auto" : "90px")};
    max-width: ${props => (props.isMobile ? "115px" : "")};
    background: #eee;
    margin: ${props => (props.isMobile ? "5px 10px" : "0px 10px")};
    outline: ${props => (props.selected ? "Thick solid var(--primary-color)" : "Thin solid white")};
    box-shadow: ${props => (props.selected ? "0px 6px 12px #515151" : "0px 2px 8px #515151")};
    transition: all 0.2s ease-in-out;
    @media (hover) {
        &:hover {
            outline: Thick solid var(--primary-hover-color);
            cursor: ${props => (props.selected ? "default" : "pointer")};
            box-shadow: 0px 6px 12px #515151;
        }
    }
`;

const LayoutMenuRow = styled.div<LayoutContainerProps>`
    display: flex;
    flex-direction: row;
    justify-content: ${props => (props.minified ? "flex-start" : "center")};
    align-items: center;
    width: 100%;
    margin-bottom: ${props => (props.minified ? "10px" : "30px")};
    ${props => (props.isMobile ? "margin-bottom: 15px" : "")};
    padding: 0px 25px;
    flex-wrap: wrap;
    pointer-events: none;
`;

const FullscreenTitle = styled.div<TitleProps>`
    background: ${props => (props.selected ? "rgba(255, 255, 255, 0.8)" : "rgba(255, 255, 255, 0.5)")};
    padding: 5px 12px;
    border-radius: 4px;
    font-weight: ${props => (props.selected ? "800" : "400")};
    pointer-events: auto;
    @media (hover) {
        &:hover {
            background-color: rgba(255, 255, 255, 0.8);
            cursor: ${props => (props.selected ? "default" : "pointer")};
        }
    }
`;

const LayoutMenuItem = styled.div<MenuProps>`
    padding: 6px 15px;
    font-size: 16px;
    margin: ${props => (props.isMobile ? "5px 5px" : "0px 9px")};
    border-radius: 4px;
    background-color: ${props => (props.selected ? "rgba(255, 255, 255, 0.9)" : "rgba(255, 255, 255, 0.5)")};
    font-weight: ${props => (props.selected ? "800" : "400")};
    transition: all 0.2s ease-in-out;
    pointer-events: auto;
    @media (hover) {
        &:hover {
            background-color: rgba(255, 255, 255, 0.9);
            cursor: ${props => (props.selected ? "default" : "pointer")};
        }
    }
`;

const LayoutArrowButtons = styled.div<LayoutGroupProps>`
    width: ${props => (props.isMobile ? "40px" : "100px")};
    height: ${props => (props.isMobile ? "40px" : "100px")};
    border-radius: 100px;
    color: white;
    margin: ${props => (props.isMobile ? "0px 0px" : "0px 10px")};
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    filter: drop-shadow(0px 1px 2px #2e2e2e) opacity(70%);
    transition: all 0.3s ease-in-out;
    pointer-events: auto;
    @media (hover) {
        &:hover {
            cursor: pointer;
            filter: drop-shadow(0px 1px 2px #2e2e2e) opacity(100%);
        }
    }
`;

const ImageSlideContainer = styled.div<LayoutGroupProps>`
    display: flex;
    align-items: center;
    ${props => (props.isMobile ? "height: 80%; width: 100%; justify-content: space-between;" : "")};
`;

const SlideIconContainer = styled.div<SlideIconProps>`
    display: flex;
    align-items: center;
    position: absolute;
    bottom: ${props => (props.isMobile ? "15px" : "-15px")};
`;

const NumSlidesIcon = styled.div<CurrentSectionProps>`
    width: 8px;
    height: 8px;
    border-radius: 20px;
    border: solid 2px white;
    box-shadow: 0px 0px 4px rgb(0 0 0 / 30%);
    background-color: ${props => (props.selected ? "rgba(255, 255, 255, 1)" : "rgba(50, 50, 50, 0.3)")};
    margin: 30px 10px 0px 10px;
    transition: all 0.3s ease-in-out;
    @media (hover) {
        &:hover {
            cursor: ${props => (props.selected ? "default" : "pointer")};
            background-color: rgba(255, 255, 255, 1);
        }
    }
`;

const Divider = styled.div`
    width: 1px;
    height: 100px;
    margin: 0px 20px;
    background-color: #fff;
`;

interface MenuProps {
    selected: Boolean;
    border: Boolean;
    isMobile: Boolean;
}

interface LayoutProps {
    selected: Boolean;
    isMobile: Boolean;
}

interface LayoutContainerProps {
    minified: Boolean;
    isMobile: Boolean;
}

interface CurrentSectionProps {
    selected: Boolean;
}

interface LayoutGroupProps {
    isMobile: Boolean;
}

interface TitleProps {
    selected: Boolean;
}

interface SlideIconProps {
    isMobile: Boolean;
}

interface LayoutGroupContainerProps {
    isActive?: Boolean;
}

export const LayoutList = ({
    layoutGroups,
    onImageClick,
    onChangeLayoutGroup,
}: {
    layoutGroups: SurfaceLayoutGroup[];
    onImageClick: Function;
    onChangeLayoutGroup?: (layoutGroup: string) => void;
}) => {
    const dispatch = useDispatch();
    const [currentSection, setCurrentSection] = useState(0);
    const [imageLoaded, setImageLoaded] = useState(true);
    const breakpoints = useBreakpoint();
    const isMobile = breakpoints.xxs || breakpoints.xs || breakpoints.sm;

    const iconUrl = useSelector(getIconUrl);
    const modelId = useSelector(getModelId);
    const guiTranslations = useSelector(getGuiTranslations);
    let activeLayoutGroup = useSelector(getActiveLayoutGroup);
    if (!layoutGroups.some(g => g.id === activeLayoutGroup)) {
        activeLayoutGroup = layoutGroups[0].id;
    }

    const changeActiveLayoutGroup = (layoutGroup: string) => {
        setCurrentSection(0);
        if (onChangeLayoutGroup) onChangeLayoutGroup(layoutGroup);
        dispatch({ type: SET_ACTIVE_LAYOUT_GROUP, activeLayoutGroup: layoutGroup });
    };

    const setActiveImages = (layoutGroup: string, layout: string) => {
        dispatch({ type: SET_ACTIVE_LAYOUT_GROUP, activeLayoutGroup: layoutGroup });
        onImageClick(layoutGroup, layout);
    };

    const currentLayouts = layoutGroups.map(g => g.layouts);
    let numLayouts = 0;
    currentLayouts.forEach(l => {
        numLayouts += l.length;
    });

    const parseFullScreen = numLayouts * IMAGE_TOTAL_SIZE < window.innerWidth * 0.7;
    const isHighResModifier = breakpoints.xxxl ? 0.5 : 0.7;

    // Need to calculate the current active layoutgroups layouts
    // If they are wider then 80%(?) of the viewport we need to
    // split it an multiple array of of items to "scroll" thrue
    let activeGroupLayouts = layoutGroups.filter(g => g.id === activeLayoutGroup).map(l => l.layouts);
    let showArrows = false;

    if (!parseFullScreen) {
        const sectionSize = isMobile ? 4 : Math.floor((window.innerWidth * isHighResModifier) / IMAGE_TOTAL_SIZE);

        // Should make prettier then needing [0]
        if (activeGroupLayouts[0].length > sectionSize) {
            showArrows = true;
            const tempArr = [];
            for (let i = 0; i < activeGroupLayouts[0].length; i += sectionSize) {
                tempArr.push(activeGroupLayouts[0].slice(i, i + sectionSize));
            }
            activeGroupLayouts = tempArr;
        }
    }

    const moveSectionRight = (value: number) => {
        if (currentSection >= activeGroupLayouts.length - 1) {
            setCurrentSection(0);
        } else {
            setCurrentSection(currentSection + value);
        }
    };

    const moveSectionLeft = (value: number) => {
        if (currentSection <= 0) {
            setCurrentSection(activeGroupLayouts.length - 1);
        } else {
            setCurrentSection(currentSection - value);
        }
    };

    const renderLayoutImage = (layoutGroup: SurfaceLayoutGroup, layout: string) => {
        const { id: groupId, activeLayout } = layoutGroup;
        const imgUrl = `${iconUrl}layouts/${modelId}_${groupId}_${layout}.jpg`;
        return (
            <LayoutImageContainer key={groupId + layout}>
                {imageLoaded ? (
                    <Layout
                        key={layout}
                        selected={layout === activeLayout}
                        onClick={() => setActiveImages(groupId, layout)}
                        src={imgUrl}
                        alt={layout}
                        onError={() => setImageLoaded(false)}
                        isMobile={isMobile}
                    />
                ) : (
                    <MissingImage
                        key={layout}
                        selected={layout === activeLayout}
                        onClick={() => setActiveImages(groupId, layout)}
                        isMobile={isMobile}
                    >
                        {modelId}_{groupId}_{layout}
                    </MissingImage>
                )}
            </LayoutImageContainer>
        );
    };

    const mobileContinerWidth = isMobile ? "100%" : "";

    return (
        <>
            {parseFullScreen ? (
                <LayoutContainer minified={parseFullScreen} isMobile={isMobile}>
                    {layoutGroups.map((g, index) => (
                        <LayoutGroupContainer key={g.id}>
                            <LayoutMenuRow minified={parseFullScreen} isMobile={isMobile}>
                                <FullscreenTitle
                                    onClick={() => {
                                        if (g.id !== activeLayoutGroup) changeActiveLayoutGroup(g.id);
                                    }}
                                    selected={g.id === activeLayoutGroup}
                                >
                                    {guiTranslations.get(`${modelId}_${g.id}`)}
                                </FullscreenTitle>
                            </LayoutMenuRow>
                            <LayoutGroup key={g.id} isMobile={isMobile}>
                                <LayoutGroupActiveContainer isActive={g.id === activeLayoutGroup}>
                                    {g.layouts.map(l => renderLayoutImage(g, l))}
                                </LayoutGroupActiveContainer>
                                {index !== layoutGroups.length - 1 ? <Divider /> : null}
                            </LayoutGroup>
                        </LayoutGroupContainer>
                    ))}
                </LayoutContainer>
            ) : (
                <LayoutContainer minified={parseFullScreen} isMobile={isMobile}>
                    <LayoutMenuRow minified={parseFullScreen} isMobile={isMobile}>
                        {layoutGroups.map((g, index) => (
                            <LayoutGroupContainer key={g.id}>
                                <LayoutMenuItem
                                    isMobile={isMobile}
                                    selected={g.id === activeLayoutGroup}
                                    border={index !== 0 && index !== layoutGroups.length - 1}
                                    onClick={() => changeActiveLayoutGroup(g.id)}
                                >
                                    {guiTranslations.get(`${modelId}_${g.id}`)}
                                </LayoutMenuItem>
                            </LayoutGroupContainer>
                        ))}
                    </LayoutMenuRow>

                    <ImageSlideContainer isMobile={isMobile}>
                        {showArrows ? (
                            <LayoutArrowButtons isMobile={isMobile} onClick={() => moveSectionLeft(1)}>
                                <ChevronLeft size={44} color="#fff" />
                            </LayoutArrowButtons>
                        ) : null}
                        {layoutGroups.map(g =>
                            g.id === activeLayoutGroup ? (
                                <LayoutGroupContainer key={g.id} style={{ width: mobileContinerWidth }}>
                                    <>
                                        <LayoutGroup key={g.id} isMobile={isMobile}>
                                            {activeGroupLayouts[currentSection].map(l => renderLayoutImage(g, l))}
                                        </LayoutGroup>
                                    </>
                                </LayoutGroupContainer>
                            ) : null
                        )}
                        {showArrows ? (
                            <LayoutArrowButtons isMobile={isMobile} onClick={() => moveSectionRight(1)}>
                                <ChevronRight size={44} color="#fff" />
                            </LayoutArrowButtons>
                        ) : null}
                    </ImageSlideContainer>
                    {activeGroupLayouts.length > 1 ? (
                        <SlideIconContainer isMobile={isMobile}>
                            {activeGroupLayouts.map((g, index) => (
                                <NumSlidesIcon
                                    selected={index === currentSection}
                                    onClick={() => setCurrentSection(index)}
                                    key={g[0]}
                                />
                            ))}
                        </SlideIconContainer>
                    ) : null}
                </LayoutContainer>
            )}
        </>
    );
};
